import React, { useCallback, useMemo, useRef, useEffect } from "react";

import { Box, Button, Typography, Table, TableHead, TableBody, TableRow, TableCell, useMediaQuery } from "@mui/material";

import { MemoizedToothRow } from "./MemoizedToothRow";

import { PERMANENT_TO_DECIDUOUS } from "../../../../config/constants/Constants";

// Define quadrants and their teeth
const QUADRANTS = {
    "upper-right-buccal": [18, 17, 16, 15, 14, 13, 12, 11],
    "upper-left-buccal": [21, 22, 23, 24, 25, 26, 27, 28],
    "lower-right-buccal": [48, 47, 46, 45, 44, 43, 42, 41],
    "lower-left-buccal": [31, 32, 33, 34, 35, 36, 37, 38],
    "upper-right-palatal": [18, 17, 16, 15, 14, 13, 12, 11],
    "upper-left-palatal": [21, 22, 23, 24, 25, 26, 27, 28],
    "lower-right-lingual": [48, 47, 46, 45, 44, 43, 42, 41],
    "lower-left-lingual": [31, 32, 33, 34, 35, 36, 37, 38]
};

// Constant value arrays - defined outside component to avoid recreation
const PROBING_DEPTH_VALUES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const RECESSION_VALUES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const FURCATION_VALUES = [1, 2, 3];
const MOBILITY_VALUES = [1, 2, 3];

export default function PeriodontalTable(props) {
    const { state, setState, chartSeparately = false, variant = "upper-right-buccal", readOnly = false } = props;

    const isMobile = useMediaQuery("@media (max-width:719.95px)");
    const isLargeScreen = useMediaQuery("@media (min-width:720px)");

    // Column visibility state for mobile - only one column visible at a time
    const [visibleColumns, setVisibleColumns] = React.useState({
        bleeding: true,
        probing: false,
        recession: false,
        furcation: false,
        sup: false,
        plaque: false,
        mobility: false
    });

    const toggleColumn = (column) => {
        // On mobile, only allow one column visible at a time
        if (isMobile) {
            setVisibleColumns({
                bleeding: column === "bleeding",
                probing: column === "probing",
                recession: column === "recession",
                furcation: column === "furcation",
                sup: column === "sup",
                plaque: column === "plaque",
                mobility: column === "mobility"
            });
        } else {
            setVisibleColumns((prev) => ({ ...prev, [column]: !prev[column] }));
        }
    };

    // Memoize derived values from variant
    const { teeth, isUpperQuadrant, isBuccalView, isPalatalView, isRightSide, viewLabel } = useMemo(() => {
        const t = QUADRANTS[variant] || QUADRANTS["upper-right-buccal"];
        const isUpper = variant.includes("upper");
        const isBuccal = variant.includes("buccal");
        const isPalatal = variant.includes("palatal");
        const isRight = variant.includes("right");
        const label = isBuccal ? "Buccal" : isPalatal ? "Palatal" : "Lingual";

        return { teeth: t, isUpperQuadrant: isUpper, isBuccalView: isBuccal, isPalatalView: isPalatal, isRightSide: isRight, viewLabel: label };
    }, [variant]);

    // Memoize row configurations based on quadrant position
    const { buccalRows, buccalLabels, palatalLingualRows, palatalLingualLabels } = useMemo(() => {
        const bRows = isRightSide ? ["db", "b", "mb"] : ["mb", "b", "db"];
        const bLabels = isRightSide ? ["distal-buccal", "buccal", "mesial-buccal"] : ["mesial-buccal", "buccal", "distal-buccal"];

        const plRows = isUpperQuadrant ? (isRightSide ? ["dp", "p", "mp"] : ["mp", "p", "dp"]) : isRightSide ? ["dl", "l", "ml"] : ["ml", "l", "dl"];
        const plLabels = isUpperQuadrant
            ? isRightSide
                ? ["distal-palatal", "palatal", "mesial-palatal"]
                : ["mesial-palatal", "palatal", "distal-palatal"]
            : isRightSide
              ? ["distal-lingual", "lingual", "mesial-lingual"]
              : ["mesial-lingual", "lingual", "distal-lingual"];

        return { buccalRows: bRows, buccalLabels: bLabels, palatalLingualRows: plRows, palatalLingualLabels: plLabels };
    }, [isRightSide, isUpperQuadrant]);

    // Memoize current rows/labels based on view type
    const { currentRows, currentLabels, supPlaqueLabels } = useMemo(() => {
        const rows = isBuccalView ? buccalRows : palatalLingualRows;
        const labels = isBuccalView ? buccalLabels : palatalLingualLabels;
        const middleLabel = isBuccalView ? "buccal" : isUpperQuadrant ? "palatal" : "lingual";
        const spLabels = isRightSide ? ["distal", middleLabel, "mesial"] : ["mesial", middleLabel, "distal"];

        return { currentRows: rows, currentLabels: labels, supPlaqueLabels: spLabels };
    }, [isBuccalView, isUpperQuadrant, isRightSide, buccalRows, buccalLabels, palatalLingualRows, palatalLingualLabels]);

    // Stable handler for updating values - uses useCallback to prevent recreation on every render
    const handleUpdate = useCallback(
        (toothNum, location, field, value) => {
            setState((prev) => ({
                ...prev,
                [toothNum]: {
                    ...prev[toothNum],
                    [location]: {
                        ...prev[toothNum][location],
                        [field]: prev[toothNum][location][field] === value ? null : value
                    }
                }
            }));
        },
        [setState]
    );

    // Stable handler for mobility updates
    const handleMobilityUpdate = useCallback(
        (toothNum, value) => {
            setState((prev) => ({
                ...prev,
                [toothNum]: {
                    ...prev[toothNum],
                    mobility: prev[toothNum].mobility === value ? null : value
                }
            }));
        },
        [setState]
    );

    // Stable handler for toggling boolean fields
    const handleToggle = useCallback(
        (toothNum, location, field) => {
            setState((prev) => ({
                ...prev,
                [toothNum]: {
                    ...prev[toothNum],
                    [location]: {
                        ...prev[toothNum][location],
                        [field]: !prev[toothNum][location][field]
                    }
                }
            }));
        },
        [setState]
    );

    // Stable handler for toggling all bleeding sites - reads state inside updater to avoid stale reads
    const handleToggleAllBleeding = useCallback(
        (toothNum, rows) => {
            setState((prev) => {
                const allSelected = rows.every((row) => prev[toothNum][row].bleeding);
                const updates = {};
                rows.forEach((row) => {
                    updates[row] = { ...prev[toothNum][row], bleeding: !allSelected };
                });
                return {
                    ...prev,
                    [toothNum]: {
                        ...prev[toothNum],
                        ...updates
                    }
                };
            });
        },
        [setState]
    );

    // --- Drag-to-select for stylus/mouse (probing depth & recession) ---
    const tableRef = useRef(null);
    const dragRef = useRef(null);
    const dragListenersRef = useRef(null);

    // A completed drag already wrote the value with SET semantics. The browser still fires a click
    // on the button the gesture ended on, and that click would run handleUpdate's TOGGLE semantics
    // and immediately clear the value again. Swallow exactly that one click.
    const suppressNextClickRef = useRef(false);

    const handleTableClickCapture = useCallback((e) => {
        if (!suppressNextClickRef.current) return;
        suppressNextClickRef.current = false;
        e.stopPropagation();
        e.preventDefault();
    }, []);

    // SET semantics — always sets the value, never toggles; skips if already matching
    const handleDragSet = useCallback(
        (toothNum, location, field, value) => {
            setState((prev) => {
                if (prev[toothNum]?.[location]?.[field] === value) return prev;
                return {
                    ...prev,
                    [toothNum]: {
                        ...prev[toothNum],
                        [location]: {
                            ...prev[toothNum][location],
                            [field]: value
                        }
                    }
                };
            });
        },
        [setState]
    );

    const handleTablePointerDown = useCallback(
        (e) => {
            // Clear first: a drag that ended off-target leaves the flag set, and it must not
            // swallow the click of the next, unrelated gesture.
            suppressNextClickRef.current = false;

            if (readOnly) return;
            if (e.pointerType === "touch") return;
            if (e.button !== 0) return;

            const btn = e.target.closest("[data-drag]");
            if (!btn || btn.disabled) return;

            const field = btn.dataset.field;
            const tooth = btn.dataset.tooth;
            const loc = btn.dataset.loc;
            const value = Number(btn.dataset.value);

            const drag = {
                active: false,
                startX: e.clientX,
                startY: e.clientY,
                pointerId: e.pointerId,
                field,
                startTooth: tooth,
                startLoc: loc,
                startValue: value,
                lastKey: null
            };
            dragRef.current = drag;

            const onMove = (ev) => {
                if (ev.pointerId !== drag.pointerId) return;

                const dx = ev.clientX - drag.startX;
                const dy = ev.clientY - drag.startY;

                if (!drag.active) {
                    if (dx * dx + dy * dy <= 64) return; // < 8px
                    // Activate drag
                    drag.active = true;
                    try {
                        tableRef.current.setPointerCapture(ev.pointerId);
                    } catch {
                        /* noop */
                    }
                    // Apply value to the starting cell
                    const startKey = `${drag.startTooth}-${drag.startLoc}`;
                    drag.lastKey = startKey;
                    handleDragSet(Number(drag.startTooth), drag.startLoc, drag.field, drag.startValue);
                }

                // Find element under pointer
                const el = document.elementFromPoint(ev.clientX, ev.clientY);
                if (!el) return;
                const target = el.closest("[data-drag]");
                if (!target || target.disabled) return;

                // Must be same field (probing_depth or recession)
                if (target.dataset.field !== drag.field) return;

                const key = `${target.dataset.tooth}-${target.dataset.loc}`;
                if (key === drag.lastKey) return;

                drag.lastKey = key;
                const val = Number(target.dataset.value);
                handleDragSet(Number(target.dataset.tooth), target.dataset.loc, drag.field, val);
            };

            const onUp = (ev) => {
                if (ev.pointerId !== drag.pointerId) return;

                if (drag.active) {
                    suppressNextClickRef.current = true;
                    try {
                        tableRef.current.releasePointerCapture(ev.pointerId);
                    } catch {
                        /* noop */
                    }
                }

                dragRef.current = null;
                dragListenersRef.current = null;
                document.removeEventListener("pointermove", onMove);
                document.removeEventListener("pointerup", onUp);
                document.removeEventListener("pointercancel", onUp);
            };

            dragListenersRef.current = { onMove, onUp };
            document.addEventListener("pointermove", onMove);
            document.addEventListener("pointerup", onUp);
            document.addEventListener("pointercancel", onUp);
        },
        [readOnly, handleDragSet]
    );

    // Cleanup on unmount — remove document listeners if mid-drag
    useEffect(() => {
        const tableEl = tableRef.current;
        return () => {
            if (dragListenersRef.current) {
                document.removeEventListener("pointermove", dragListenersRef.current.onMove);
                document.removeEventListener("pointerup", dragListenersRef.current.onUp);
                document.removeEventListener("pointercancel", dragListenersRef.current.onUp);
            }
            if (dragRef.current?.active && tableEl && dragRef.current.pointerId != null) {
                try {
                    tableEl.releasePointerCapture(dragRef.current.pointerId);
                } catch {
                    /* noop */
                }
            }
        };
    }, []);

    // Helper to get effective tooth for a permanent position
    // Returns deciduous tooth if it exists and permanent is missing
    const getEffectiveTooth = (permanentToothNum) => {
        const deciduousTooth = PERMANENT_TO_DECIDUOUS[permanentToothNum];

        if (deciduousTooth && state[deciduousTooth] && !state[deciduousTooth].missing && state[permanentToothNum]?.missing) {
            return deciduousTooth;
        }

        return permanentToothNum;
    };

    // Tooth-specific furcation configuration - memoized to prevent recreation
    const getFurcationConfig = useCallback(
        (toothNum) => {
            if (isBuccalView) {
                // Upper jaw buccal
                if ([18, 17, 16].includes(toothNum)) {
                    return { show: true, rows: ["db", "b", "mb"], labels: ["distal", "buccal", "mesial"], isEmpty: [false, false, false] };
                } else if ([15, 14].includes(toothNum)) {
                    return { show: true, rows: ["db", null, "mb"], labels: ["distal", "", "mesial"], isEmpty: [false, true, false] };
                } else if ([26, 27, 28].includes(toothNum)) {
                    return { show: true, rows: ["mb", "b", "db"], labels: ["mesial", "buccal", "distal"], isEmpty: [false, false, false] };
                } else if ([24, 25].includes(toothNum)) {
                    return { show: true, rows: ["mb", null, "db"], labels: ["mesial", "", "distal"], isEmpty: [false, true, false] };
                }
                // Lower jaw buccal
                else if ([48, 47, 46].includes(toothNum)) {
                    return { show: true, rows: [null, "b", null], labels: ["", "buccal", ""], isEmpty: [true, false, true] };
                } else if ([45, 44].includes(toothNum)) {
                    return { show: true, rows: ["db", null, "mb"], labels: ["distal", "", "mesial"], isEmpty: [false, true, false] };
                } else if ([36, 37, 38].includes(toothNum)) {
                    return { show: true, rows: [null, "b", null], labels: ["", "buccal", ""], isEmpty: [true, false, true] };
                } else if ([34, 35].includes(toothNum)) {
                    return { show: true, rows: ["mb", null, "db"], labels: ["mesial", "", "distal"], isEmpty: [false, true, false] };
                }
            } else if (isPalatalView) {
                // Upper jaw palatal
                if ([18, 17, 16].includes(toothNum)) {
                    return { show: true, rows: ["dp", null, "mp"], labels: ["distal", "", "mesial"], isEmpty: [false, true, false] };
                } else if ([15, 14].includes(toothNum)) {
                    return { show: true, rows: ["dp", null, "mp"], labels: ["distal", "", "mesial"], isEmpty: [false, true, false] };
                } else if ([24, 25].includes(toothNum)) {
                    return { show: true, rows: ["mp", null, "dp"], labels: ["mesial", "", "distal"], isEmpty: [false, true, false] };
                } else if ([26, 27, 28].includes(toothNum)) {
                    return { show: true, rows: ["mp", null, "dp"], labels: ["mesial", "", "distal"], isEmpty: [false, true, false] };
                }
            } else {
                // Lower jaw lingual
                if ([48, 47, 46].includes(toothNum)) {
                    return { show: true, rows: [null, "l", null], labels: ["", "lingual", ""], isEmpty: [true, false, true] };
                } else if ([45, 44].includes(toothNum)) {
                    return { show: true, rows: ["dl", null, "ml"], labels: ["distal", "", "mesial"], isEmpty: [false, true, false] };
                } else if ([36, 37, 38].includes(toothNum)) {
                    return { show: true, rows: [null, "l", null], labels: ["", "lingual", ""], isEmpty: [true, false, true] };
                } else if ([34, 35].includes(toothNum)) {
                    return { show: true, rows: ["ml", null, "dl"], labels: ["mesial", "", "distal"], isEmpty: [false, true, false] };
                }
            }

            return { show: false, rows: [], labels: [], isEmpty: [] };
        },
        [isBuccalView, isPalatalView]
    );

    return (
        <Box
            id={`table-${variant}`}
            ref={tableRef}
            onPointerDown={handleTablePointerDown}
            onClickCapture={handleTableClickCapture}
            sx={{ width: "100%", overflowX: "auto", touchAction: "auto" }}>
            {/* Header */}
            <Box sx={{ mb: 1, p: 1, bgcolor: "background.secondary", borderRadius: 1 }}>
                <Typography variant="h5" align="center">
                    {viewLabel}
                </Typography>
            </Box>

            {/* Mobile Column Toggle */}
            {isMobile && (
                <Box sx={{ mb: 1, p: 1, bgcolor: "background.secondary", borderRadius: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    <Typography variant="caption" sx={{ width: "100%", mb: 0.5, fontWeight: "bold" }}>
                        Show/Hide Columns:
                    </Typography>
                    {(chartSeparately
                        ? [
                              { key: "bleeding", label: "Bleeding" },
                              { key: "probing", label: "Probing" },
                              { key: "recession", label: "Recession" },
                              { key: "furcation", label: "Furcation" },
                              { key: "sup", label: "SUP" },
                              { key: "mobility", label: "Mobility" }
                          ]
                        : [
                              { key: "bleeding", label: "Bleeding" },
                              { key: "probing", label: "Probing" },
                              { key: "recession", label: "Recession" },
                              { key: "furcation", label: "Furcation" },
                              { key: "sup", label: "SUP" },
                              { key: "plaque", label: "Plaque" },
                              { key: "mobility", label: "Mobility" }
                          ]
                    ).map(({ key, label }) => (
                        <Button
                            key={key}
                            size="small"
                            variant={visibleColumns[key] ? "contained" : "outlined"}
                            onClick={() => toggleColumn(key)}
                            sx={{ fontSize: "0.85rem", p: 0.4, textTransform: "none" }}>
                            {label}
                        </Button>
                    ))}
                </Box>
            )}

            {/* Main Table */}
            <Table sx={{ border: "1px solid #ddd", borderCollapse: "separate", borderSpacing: 0, tableLayout: isLargeScreen ? "fixed" : "auto" }} size="small">
                <TableHead>
                    <TableRow>
                        {/* Tooth Number Column - Always visible on mobile */}
                        {isMobile && (
                            <TableCell
                                align="center"
                                sx={{
                                    width: 30,
                                    minWidth: 30,
                                    p: 0.5,
                                    bgcolor: "background.secondary",
                                    fontWeight: "bold",
                                    border: "1px solid #ddd",
                                    fontSize: "0.8rem"
                                }}>
                                Nr
                            </TableCell>
                        )}
                        {(!isMobile || visibleColumns.bleeding) && (
                            <TableCell
                                align="center"
                                sx={{
                                    bgcolor: "background.red",
                                    fontWeight: "bold",
                                    border: "1px solid #ddd",
                                    p: 0.5,
                                    fontSize: "0.85rem",
                                    width: isLargeScreen ? "12%" : "auto",
                                    minWidth: isMobile ? 120 : "auto"
                                }}>
                                Bleeding sites
                            </TableCell>
                        )}
                        {(!isMobile || visibleColumns.probing) && (
                            <TableCell
                                align="center"
                                sx={{
                                    bgcolor: "background.lightblue",
                                    fontWeight: "bold",
                                    border: "1px solid #ddd",
                                    p: 0.5,
                                    fontSize: "0.85rem",
                                    width: isLargeScreen ? "26%" : "auto",
                                    minWidth: isMobile ? 220 : "auto"
                                }}>
                                Probing Depth mm
                            </TableCell>
                        )}
                        {(!isMobile || visibleColumns.recession) && (
                            <TableCell
                                align="center"
                                sx={{
                                    bgcolor: "background.gray",
                                    fontWeight: "bold",
                                    border: "1px solid #ddd",
                                    p: 0.5,
                                    fontSize: "0.85rem",
                                    width: isLargeScreen ? "26%" : "auto",
                                    minWidth: isMobile ? 220 : "auto"
                                }}>
                                Recession (mm from CEJ)
                            </TableCell>
                        )}
                        {(!isMobile || visibleColumns.furcation) && (
                            <TableCell
                                align="center"
                                sx={{
                                    bgcolor: "background.blue",
                                    fontWeight: "bold",
                                    border: "1px solid #ddd",
                                    p: 0.5,
                                    fontSize: "0.85rem",
                                    width: isLargeScreen ? "12%" : "auto",
                                    minWidth: isMobile ? 120 : "auto"
                                }}>
                                Furcation Class
                            </TableCell>
                        )}
                        {(!isMobile || visibleColumns.sup) && (
                            <TableCell
                                align="center"
                                sx={{
                                    bgcolor: "background.orange",
                                    fontWeight: "bold",
                                    border: "1px solid #ddd",
                                    p: 0.5,
                                    fontSize: "0.85rem",
                                    width: isLargeScreen ? "8%" : "auto",
                                    minWidth: isMobile ? 70 : "auto"
                                }}>
                                SUP
                            </TableCell>
                        )}
                        {!chartSeparately && (!isMobile || visibleColumns.plaque) && (
                            <TableCell
                                align="center"
                                sx={{
                                    bgcolor: "background.purple",
                                    fontWeight: "bold",
                                    border: "1px solid #ddd",
                                    p: 0.5,
                                    fontSize: "0.85rem",
                                    width: isLargeScreen ? "8%" : "auto",
                                    minWidth: isMobile ? 70 : "auto"
                                }}>
                                Plaque
                            </TableCell>
                        )}
                        {(!isMobile || visibleColumns.mobility) && (
                            <TableCell
                                align="center"
                                sx={{
                                    bgcolor: "background.green",
                                    fontWeight: "bold",
                                    border: "1px solid #ddd",
                                    p: 0.5,
                                    fontSize: "0.85rem",
                                    width: isLargeScreen ? "8%" : "auto",
                                    minWidth: isMobile ? 70 : "auto"
                                }}>
                                Mobility
                            </TableCell>
                        )}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {teeth.map((permanentToothNum, index) => {
                        // Check if a deciduous tooth should be shown instead
                        const effectiveToothNum = getEffectiveTooth(permanentToothNum);
                        const toothData = state[effectiveToothNum];
                        // Use permanent tooth position for furcation labels
                        const furcationConfig = getFurcationConfig(permanentToothNum);

                        return (
                            <MemoizedToothRow
                                key={permanentToothNum}
                                toothNum={effectiveToothNum}
                                toothData={toothData}
                                index={index}
                                furcationConfig={furcationConfig}
                                currentRows={currentRows}
                                currentLabels={currentLabels}
                                supPlaqueLabels={supPlaqueLabels}
                                probingDepthValues={PROBING_DEPTH_VALUES}
                                recessionValues={RECESSION_VALUES}
                                furcationValues={FURCATION_VALUES}
                                mobilityValues={MOBILITY_VALUES}
                                isMobile={isMobile}
                                isLargeScreen={isLargeScreen}
                                visibleColumns={visibleColumns}
                                chartSeparately={chartSeparately}
                                readOnly={readOnly}
                                handleUpdate={handleUpdate}
                                handleToggle={handleToggle}
                                handleMobilityUpdate={handleMobilityUpdate}
                                handleToggleAllBleeding={handleToggleAllBleeding}
                            />
                        );
                    })}
                </TableBody>
            </Table>
        </Box>
    );
}
